import { ContentNugget } from '@backrs_ui/common-ui';

import { toAgentContextNugget } from '@backrs_ui/grow-ui/src/agentContext/toAgentContextNugget';
import { useRegisterAgentContext } from '@backrs_ui/grow-ui/src/agentContext/useRegisterAgentContext';

import { internshipGuideContent } from '../content';

const mythsDetail = [
  internshipGuideContent.mythsVsReality.intro,
  ...internshipGuideContent.mythsVsReality.pairs.map(
    (pair) =>
      `Myth: ${pair.myth.title} — ${pair.myth.body} | Reality: ${pair.reality.title} — ${pair.reality.body}`,
  ),
].join('\n');

type UseAgentContextArgs = Readonly<{
  skillNuggets: readonly ContentNugget[];
}>;

export const useAgentContext = ({ skillNuggets }: UseAgentContextArgs) => {
  useRegisterAgentContext({
    kind: 'section',
    title: 'Overview',
    description:
      'Introductory section about why landing a great internship matters, with highlight stats on hidden opportunities, targeted applications, and interview volume.',
    detail: [
      internshipGuideContent.header.description.join('\n'),
      ...internshipGuideContent.overviewStats.map((stat) => `${stat.title}: ${stat.description}`),
    ].join('\n'),
  });

  useRegisterAgentContext({
    kind: 'section',
    title: internshipGuideContent.mythsVsReality.title,
    description:
      'Compares common internship myths with the reality, covering job boards vs networking, AI vs experience, application volume vs quality, and effort level.',
    detail: mythsDetail,
  });

  useRegisterAgentContext({
    kind: 'section',
    title: 'Internship Tools',
    description:
      'Cards linking to internship search, resume builder, resume optimizer, and profile booster tools.',
  });

  useRegisterAgentContext({
    kind: 'section',
    title: internshipGuideContent.sixStepProcess.title,
    description:
      'Explains running the internship search like a sales funnel using a simple tracker, then walks through six steps in an accordion: Recruit a Team, Set Targets, Build a Strategy, Engage, Get Offers, and Make the Most of It.',
    detail: [
      ...internshipGuideContent.sixStepProcess.paragraphs,
      ...internshipGuideContent.sixStepProcess.steps.map(
        (step) => `${step.number}. ${step.title}: ${step.intro}`,
      ),
      `${internshipGuideContent.conclusion.title}: ${internshipGuideContent.conclusion.subtitle}`,
      ...internshipGuideContent.conclusion.paragraphs,
    ].join('\n'),
  });

  useRegisterAgentContext({
    kind: 'section',
    title: internshipGuideContent.skillBuilding.title,
    description:
      'A horizontally scrolling row of Backrs competencies most relevant to landing an internship, each linking to its competency page.',
    detail: internshipGuideContent.skillBuilding.subtitle,
  });

  useRegisterAgentContext(
    skillNuggets.length > 0
      ? {
          kind: 'nuggetList',
          description:
            'Backrs competencies shown in the internship skill-building row. Use these ids when the user asks about them.',
          nuggets: skillNuggets.map(toAgentContextNugget),
        }
      : null,
  );

  useRegisterAgentContext({
    kind: 'section',
    title: internshipGuideContent.adviceFromAPro.title,
    description:
      'Advice from Backrs co-founder Tom Latkovic, a former McKinsey Senior Partner with decades of hiring experience, paired with an embedded video on landing an internship that opens doors.',
    detail: [
      internshipGuideContent.adviceFromAPro.intro,
      'What Tom covers in the video:',
      internshipGuideContent.adviceFromAPro.videoSummary,
    ].join('\n'),
  });
};
